import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "@/components/ui/sonner";
import { useConfiguracaoAcessos, useSalvarConfiguracaoAcessos, type ConfiguracaoAcessos } from "./api";
import { configuracaoSchema } from "./schema";

const CAMPOS: { grupo: string; campos: { name: keyof ConfiguracaoAcessos; label: string }[] }[] = [
  {
    grupo: "Instituição",
    campos: [
      { name: "diasAtencaoInstituicao", label: "Atenção a partir de (dias sem acesso)" },
      { name: "diasInativaInstituicao", label: "Inativa a partir de (dias sem acesso)" },
    ],
  },
  {
    grupo: "Usuário",
    campos: [
      { name: "diasAtencaoUsuario", label: "Atenção a partir de (dias sem acesso)" },
      { name: "diasInativoUsuario", label: "Inativo a partir de (dias sem acesso)" },
    ],
  },
];

export function ConfiguracaoAcessosDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const config = useConfiguracaoAcessos();
  const salvar = useSalvarConfiguracaoAcessos();
  const form = useForm<ConfiguracaoAcessos>({ resolver: zodResolver(configuracaoSchema) });

  useEffect(() => {
    if (open && config.data) form.reset(config.data);
  }, [open, config.data, form]);

  const onSubmit = form.handleSubmit((valores) =>
    salvar.mutate(valores, {
      onSuccess: () => {
        toast.success("Limites salvos.");
        onOpenChange(false);
      },
    }),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Limites de inatividade</DialogTitle>
          <DialogDescription>
            Dias sem nenhum login para marcar como "atenção" e "inativa". Vale para todos que usam o painel.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-5" noValidate>
            {CAMPOS.map(({ grupo, campos }) => (
              <fieldset key={grupo} className="space-y-3">
                <legend className="text-sm font-semibold text-foreground">{grupo}</legend>
                <div className="grid gap-3 sm:grid-cols-2">
                  {campos.map((c) => (
                    <FormField
                      key={c.name}
                      control={form.control}
                      name={c.name}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{c.label}</FormLabel>
                          <FormControl>
                            <Input type="number" min={1} {...field} value={field.value ?? ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </fieldset>
            ))}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={salvar.isPending || !config.data}>
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
